import { api } from './api';
import { storage } from './storage';

export type SyncStatus = 'synced' | 'syncing' | 'error' | 'outdated';

export interface SyncEvent {
  type: 'document_changed' | 'sync_complete' | 'sync_error';
  documentId?: string;
  newVersion?: number;
}

type SyncCallback = (event: SyncEvent) => void;

class SyncService {
  private intervalId: number | null = null;
  private callbacks: SyncCallback[] = [];
  private syncInterval = 30000; // 30 segundos

  start(): void {
    if (this.intervalId) return;

    this.intervalId = window.setInterval(() => {
      this.checkForChanges();
    }, this.syncInterval);

    // Check inicial
    this.checkForChanges();
  }

  stop(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  onSync(callback: SyncCallback): () => void {
    this.callbacks.push(callback);
    return () => {
      this.callbacks = this.callbacks.filter(cb => cb !== callback);
    };
  }

  private emit(event: SyncEvent): void {
    for (const callback of this.callbacks) {
      callback(event);
    }
  }

  async checkForChanges(): Promise<void> {
    try {
      const response = await api.checkSync();

      if (!response.success || !response.data) {
        this.emit({ type: 'sync_error' });
        return;
      }

      const openTabs = storage.getOpenTabs();

      for (const doc of response.data.documents) {
        const localVersion = storage.getLocalVersion(doc.id);
        const isOpen = openTabs.some(t => t.id === doc.id);

        if (isOpen && doc.version > localVersion) {
          // Hay una versión más nueva en el servidor
          this.emit({
            type: 'document_changed',
            documentId: doc.id,
            newVersion: doc.version
          });
        }
      }

      this.emit({ type: 'sync_complete' });
    } catch (error) {
      console.error('Sync error:', error);
      this.emit({ type: 'sync_error' });
    }
  }

  async checkDocument(id: string): Promise<boolean> {
    try {
      const response = await api.checkSyncDocument(id);

      if (!response.success || !response.data) {
        return false;
      }

      const localVersion = storage.getLocalVersion(id);
      return response.data.version > localVersion;
    } catch {
      return false;
    }
  }
}

export const syncService = new SyncService();
